/**
 * Feature-based logging utility
 * 
 * Logs can be enabled/disabled per feature via localStorage.
 * Settings persist across sessions and can be toggled via the dev tools modal.
 * 
 * Only active in development environments (dev, development).
 */

// Log categories/features
export type LogCategory = 
  | 'dataManager'
  | 'subscriptions'
  | 'dashboard'
  | 'calendar'
  | 'lessons'
  | 'profile'
  | 'auth'
  | 'cancellation'
  | 'invoices'
  | 'families'
  | 'students'
  | 'locations'
  | 'api'
  | 'graphql'
  | 'subscription-billing'
  | 'build'
  | 'navigation';

// Category display names and descriptions
export const LOG_CATEGORIES: Record<LogCategory, { name: string; description: string; emoji: string }> = {
  dataManager: { name: 'Data Manager', description: 'Centralized data loading and caching', emoji: '📦' },
  subscriptions: { name: 'Subscriptions', description: 'Real-time WebSocket subscriptions', emoji: '📡' },
  dashboard: { name: 'Dashboard', description: 'Dashboard page and widgets', emoji: '📊' },
  calendar: { name: 'Calendar', description: 'Calendar views and scheduling', emoji: '📅' },
  lessons: { name: 'Lessons', description: 'Lesson management and display', emoji: '📚' },
  profile: { name: 'Profile', description: 'User profile operations', emoji: '👤' },
  auth: { name: 'Authentication', description: 'Login, logout, session management', emoji: '🔐' },
  cancellation: { name: 'Cancellation', description: 'Lesson cancellation logic', emoji: '❌' },
  invoices: { name: 'Invoices', description: 'Invoice generation and management', emoji: '💰' },
  families: { name: 'Families', description: 'Family management', emoji: '👨‍👩‍👧' },
  students: { name: 'Students', description: 'Student management', emoji: '🎓' },
  locations: { name: 'Locations', description: 'Location management', emoji: '📍' },
  api: { name: 'REST API', description: 'REST API calls and responses', emoji: '🌐' },
  graphql: { name: 'GraphQL', description: 'GraphQL queries and mutations', emoji: '⚡' },
  'subscription-billing': { name: 'Billing', description: 'Subscription and billing', emoji: '💳' },
  build: { name: 'Build Info', description: 'Build and environment info', emoji: '🏗️' },
  navigation: { name: 'Navigation', description: 'Route navigation and redirects', emoji: '🧭' },
};

const STORAGE_KEY = 'stagenote_log_settings';

// Default: all logs disabled except errors
const DEFAULT_SETTINGS: Record<LogCategory, boolean> = {
  dataManager: false,
  subscriptions: false,
  dashboard: false,
  calendar: false,
  lessons: false,
  profile: false,
  auth: false,
  cancellation: false,
  invoices: false,
  families: false,
  students: false,
  locations: false,
  api: false,
  graphql: false,
  'subscription-billing': false,
  build: false,
  navigation: false,
};

// Check if we're in a dev environment
const isDevEnvironment = (): boolean => {
  if (typeof window === 'undefined') return false;
  const env = import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development';
  return env === 'dev' || env === 'development';
};

// Get settings from localStorage
export const getLogSettings = (): Record<LogCategory, boolean> => {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new categories
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_SETTINGS;
};

// Save settings to localStorage
export const setLogSettings = (settings: Record<LogCategory, boolean>): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage errors
  }
};

// Enable a single category
export const enableLogCategory = (category: LogCategory): void => {
  const settings = getLogSettings();
  settings[category] = true;
  setLogSettings(settings);
};

// Disable a single category
export const disableLogCategory = (category: LogCategory): void => {
  const settings = getLogSettings();
  settings[category] = false;
  setLogSettings(settings);
};

// Toggle a single category
export const toggleLogCategory = (category: LogCategory): boolean => {
  const settings = getLogSettings();
  settings[category] = !settings[category];
  setLogSettings(settings);
  return settings[category];
};

// Enable all categories
export const enableAllLogs = (): void => {
  const settings = getLogSettings();
  Object.keys(settings).forEach(key => {
    settings[key as LogCategory] = true;
  });
  setLogSettings(settings);
};

// Disable all categories
export const disableAllLogs = (): void => {
  setLogSettings(DEFAULT_SETTINGS);
};

// Check if a category is enabled
export const isLogEnabled = (category: LogCategory): boolean => {
  if (!isDevEnvironment()) return false;
  return getLogSettings()[category] ?? false;
};

// No-op function for disabled logs
const noop = (): void => {};

// The main logger class
// Uses getters that return bound console methods to preserve call site in browser dev tools
class Logger {
  private category: LogCategory;
  private emoji: string;
  private prefix: string;
  private successPrefix: string;

  constructor(category: LogCategory) {
    this.category = category;
    const config = LOG_CATEGORIES[category];
    this.emoji = config?.emoji || '📝';
    this.prefix = `${this.emoji} [${config?.name || category}]`;
    this.successPrefix = `✅ ${this.prefix}`;
  }

  private isEnabled(): boolean {
    return isLogEnabled(this.category);
  }

  // Using getters that return bound console methods preserves the call site
  // in the browser's dev tools (clicking the log shows where it was called, not logger.ts)
  
  get log(): (...args: any[]) => void {
    if (!this.isEnabled()) return noop;
    return console.log.bind(console, this.prefix);
  }

  get info(): (...args: any[]) => void {
    if (!this.isEnabled()) return noop;
    return console.info.bind(console, this.prefix);
  }

  get warn(): (...args: any[]) => void {
    // Warnings always show in dev
    if (!isDevEnvironment()) return noop;
    return console.warn.bind(console, this.prefix);
  }

  get error(): (...args: any[]) => void {
    // Errors always show
    return console.error.bind(console, this.prefix);
  }

  get debug(): (...args: any[]) => void {
    if (!this.isEnabled()) return noop;
    return console.debug.bind(console, this.prefix);
  }

  // Log with success checkmark
  get success(): (...args: any[]) => void {
    if (!this.isEnabled()) return noop;
    return console.log.bind(console, this.successPrefix);
  }

  // Log a group of related messages (can't use getter pattern for this)
  group(label: string, fn: () => void): void {
    if (this.isEnabled()) {
      console.group(`${this.prefix} ${label}`);
      fn();
      console.groupEnd();
    }
  }

  // Log a table (can't use getter pattern for this)
  table(data: any): void {
    if (this.isEnabled()) {
      console.log(this.prefix);
      console.table(data);
    }
  }

  // Time an operation
  time(label: string): void {
    if (this.isEnabled()) {
      console.time(`${this.prefix} ${label}`);
    }
  }

  timeEnd(label: string): void {
    if (this.isEnabled()) {
      console.timeEnd(`${this.prefix} ${label}`);
    }
  }
}

// Pre-created logger instances for each category
export const loggers = {
  dataManager: new Logger('dataManager'),
  subscriptions: new Logger('subscriptions'),
  dashboard: new Logger('dashboard'),
  calendar: new Logger('calendar'),
  lessons: new Logger('lessons'),
  profile: new Logger('profile'),
  auth: new Logger('auth'),
  cancellation: new Logger('cancellation'),
  invoices: new Logger('invoices'),
  families: new Logger('families'),
  students: new Logger('students'),
  locations: new Logger('locations'),
  api: new Logger('api'),
  graphql: new Logger('graphql'),
  subscriptionBilling: new Logger('subscription-billing'),
  build: new Logger('build'),
  navigation: new Logger('navigation'),
};

// Factory function to create a logger for a category
export const createLogger = (category: LogCategory): Logger => new Logger(category);

// Export Logger class for custom usage
export { Logger };

// Default export
export default loggers;
